/* ============================================================================
 * DEPRECATED — DO NOT MODIFY.
 *
 * This file is part of the legacy V1 playback pipeline. Audio playback is
 * being migrated to the V2 engine's WebAudioManager
 * (src/Studio/engine/WebAudioManager.ts) + _syncAllAudio path.
 *
 * Rules (enforced by memory/feedback_v1_deprecation.md):
 *   - Bug fixes on playback go in V2 or as a V2 port. Do not patch here.
 *   - New features do not land here.
 *   - Only the removal phase itself may edit this file.
 *
 * Migration status: Phase 4 in progress.
 * ============================================================================ */

/**
 * AudioManager — HTMLAudioElement sync for timeline audio clips (LEGACY V1)
 *
 * This module manages audio playback for audio-only clips (mp3, wav, etc.)
 * that are separate from video elements. It mirrors the video sync logic
 * but for standalone audio assets.
 *
 * PHASE 1 ADDITIONS:
 * - Volume keyframe interpolation (calculateVolumeWithKeyframes)
 * - Audio ducking (detectVoiceActivity, applyDucking)
 */

import type { Clip, Asset, Track, AudioAdjustments, Keyframe } from "./EditorStore";
import { getRampedValue } from "./ramp";

/**
 * Effect modifier from effect regions - used to override clip settings
 * when playhead is inside an effect region.
 */
export type RegionEffect = {
  volume?: number;  // 0-200 (100 = normal)
  speed?: number;   // 0.25-4 (1 = normal) - Region FX playback multiplier
  rampMs?: number;
  regionId?: string;
  targetClipId?: string;        // deprecated: use targetClipIds
  targetClipIds?: string[];     // multi-element support
};

export type AudioMap = Map<string, HTMLAudioElement>;

// Module-level state for audio sync
const audioSyncState = {
  lastSeekTime: new Map<string, number>(),
  prevPlayhead: 0,
  // Track which clip IDs are currently active (for multi-clip same-asset scenarios)
  activeClipIds: new Set<string>(),
  playbackStartMs: 0,
};

// PHASE 1: Module-level state for audio ducking (per-clip)
const duckingState = new Map<string, { active: boolean; rampProgress: number }>();

// 🔧 OPTIMIZED: Sync constants for smooth audio playback
// Tighter tolerances for audio since it's more noticeable than video
const SOFT_DRIFT_TOLERANCE = 0.06;   // 60ms - earlier rate correction (was 150ms)
const HARD_DRIFT_TOLERANCE = 0.25;   // 250ms - force seek (was 400ms)
const SEEK_COOLDOWN_MS = 1000;       // 1s cooldown (was 2s) - faster audio recovery
const RATE_CORRECTION_MIN = 0.985;   // Very gentle (was 0.97) - minimally audible
const RATE_CORRECTION_MAX = 1.015;   // Very gentle (was 1.03) - minimally audible
const SCRUB_TOLERANCE = 0.016;       // 16ms for precise scrubbing
const PLAYHEAD_WINDOW_EPS = 1 / 240;

/**
 * Check if timeline time t is inside a clip's visible interval [start, end)
 */
function isTimeInsideClip(clip: Clip, t: number): boolean {
  return t + PLAYHEAD_WINDOW_EPS >= clip.start && t <= clip.end + PLAYHEAD_WINDOW_EPS;
}

/**
 * Compute the source media time for a given clip at timeline time t.
 */
function mediaTimeForClip(clip: Clip, t: number): number {
  const speed = clip.speed ?? 1;
  const rel = Math.max(0, t - clip.start);
  return clip.in + rel * speed;
}

const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));

const DEFAULT_VOL_RAMP_MS = 120;
const DEFAULT_SPEED_RAMP_MS = 200;
const volumeRampMsFor = (regionEffect?: RegionEffect | null) => {
  const base = regionEffect?.rampMs;
  if (typeof base === "number" && Number.isFinite(base)) return clamp(base, 100, 150);
  return DEFAULT_VOL_RAMP_MS;
};
const speedRampMsFor = (regionEffect?: RegionEffect | null) => {
  const base = regionEffect?.rampMs;
  if (typeof base === "number" && Number.isFinite(base)) return clamp(base, 150, 250);
  return DEFAULT_SPEED_RAMP_MS;
};

const regionVolumeRamp = new Map<string, import("./ramp").RampState>();
const regionSpeedRamp = new Map<string, import("./ramp").RampState>();

/**
 * Calculate volume with fade in/out envelope applied
 */
function calculateVolumeWithFades(
  clip: Clip,
  playhead: number,
  baseVolume: number
): number {
  const audio = clip.audio;
  if (!audio) return baseVolume;

  const clipDuration = clip.end - clip.start;
  const positionInClip = playhead - clip.start;

  let volumeMultiplier = 1;

  // Apply fade in
  const fadeIn = audio.fadeIn ?? 0;
  if (fadeIn > 0 && positionInClip < fadeIn) {
    volumeMultiplier *= positionInClip / fadeIn;
  }

  // Apply fade out
  const fadeOut = audio.fadeOut ?? 0;
  if (fadeOut > 0) {
    const fadeOutStart = clipDuration - fadeOut;
    if (positionInClip > fadeOutStart) {
      const fadeOutProgress = (clipDuration - positionInClip) / fadeOut;
      volumeMultiplier *= Math.max(0, fadeOutProgress);
    }
  }

  return baseVolume * volumeMultiplier;
}

/**
 * PHASE 1: Calculate volume with keyframe automation applied
 *
 * This function interpolates volume between keyframes for smooth automation.
 * It applies ON TOP of fade in/out and base volume.
 *
 * @param clip - The clip being played
 * @param playhead - Current timeline playhead position (seconds)
 * @param baseVolume - Volume after fades applied (0-2 range, where 1 = 100%)
 * @param keyframes - All keyframes from timeline (will be filtered for this clip)
 * @returns Interpolated volume value
 */
export function calculateVolumeWithKeyframes(
  clip: Clip,
  playhead: number,
  baseVolume: number,
  keyframes: Keyframe[]
): number {
  // Filter for volume keyframes on this clip
  const volumeKfs = keyframes
    .filter((k) => k.clipId === clip.id && k.property === 'audio.volume')
    .sort((a, b) => a.time - b.time);

  // No keyframes: return base volume unchanged
  if (volumeKfs.length === 0) return baseVolume;

  // Calculate relative time within clip
  const relativeTime = playhead - clip.start;

  // Find surrounding keyframes
  let beforeKf: Keyframe | null = null;
  let afterKf: Keyframe | null = null;

  for (let i = 0; i < volumeKfs.length; i++) {
    if (volumeKfs[i].time <= relativeTime) {
      beforeKf = volumeKfs[i];
    }
    if (volumeKfs[i].time >= relativeTime && !afterKf) {
      afterKf = volumeKfs[i];
    }
  }

  // Determine keyframe volume multiplier (0-2 range, where 1 = 100%)
  let keyframeVolMultiplier = 1;

  // Exact match
  if (beforeKf && beforeKf.time === relativeTime) {
    const val = typeof beforeKf.value === 'number' ? beforeKf.value : 100;
    keyframeVolMultiplier = val / 100; // Convert from 0-200% to 0-2 range
  }
  // Before first keyframe: use first keyframe value
  else if (!beforeKf && afterKf) {
    const val = typeof afterKf.value === 'number' ? afterKf.value : 100;
    keyframeVolMultiplier = val / 100;
  }
  // After last keyframe: use last keyframe value
  else if (beforeKf && !afterKf) {
    const val = typeof beforeKf.value === 'number' ? beforeKf.value : 100;
    keyframeVolMultiplier = val / 100;
  }
  // Interpolate between two keyframes
  else if (beforeKf && afterKf) {
    const beforeVal = typeof beforeKf.value === 'number' ? beforeKf.value : 100;
    const afterVal = typeof afterKf.value === 'number' ? afterKf.value : 100;
    const t = (relativeTime - beforeKf.time) / (afterKf.time - beforeKf.time);
    const interpolatedVal = beforeVal + (afterVal - beforeVal) * t;
    keyframeVolMultiplier = interpolatedVal / 100;
  }

  // Apply keyframe volume on top of base volume (fades already applied)
  return baseVolume * keyframeVolMultiplier;
}

/**
 * Create or get an HTMLAudioElement for an audio asset
 */
export function getOrCreateAudioElement(
  assetId: string,
  src: string,
  audioMap: AudioMap
): HTMLAudioElement {
  let audio = audioMap.get(assetId);

  if (audio) {
    // Check if src changed
    const existingSrc = (audio as any).__vbSrc as string | undefined;
    if (existingSrc === src) {
      return audio;
    }
    // Src changed, clean up old element
    try { audio.pause(); } catch {}
    audioMap.delete(assetId);
  }

  // Create new audio element
  audio = document.createElement('audio');
  (audio as any).__vbSrc = src;
  audio.crossOrigin = 'anonymous';
  // 🔧 FIX: Use 'auto' preload for better buffering and reduced stutter
  audio.preload = 'auto';
  audio.src = src;

  // 🔧 FIX: Pre-buffer audio by loading it
  audio.load();

  audioMap.set(assetId, audio);
  return audio;
}

/**
 * Sync audio clips at the current playhead position.
 * This is the main function that handles audio-only clip playback.
 *
 * PHASE 1: Now supports volume keyframes for automation.
 *
 * @param regionEffect - Optional effect region modifier (volume/speed override)
 */
export function syncAudioClipsAtPlayhead(
  playhead: number,
  clips: Clip[],
  assets: Asset[],
  tracks: Track[],
  audioMap: AudioMap,
  masterMuted: boolean,
  isPlaying: boolean,
  keyframes: Keyframe[] = [],
  regionEffect?: RegionEffect | null
): void {
  const assetsById = new Map<string, Asset>(assets.map(a => [a.id, a]));
  const tracksById = new Map<string, Track>(tracks.map(t => [t.id, t]));
  const activeAssetIds = new Set<string>();

  // Detect jump/scrub
  const playheadDelta = Math.abs(playhead - audioSyncState.prevPlayhead);
  const isJump = playheadDelta > 0.5;
  audioSyncState.prevPlayhead = playhead;

  const now = performance.now();

  // Check for solo tracks
  const hasSolo = tracks.some(t => (t.kind === "audio" || t.kind === "video") && t.solo);

  // Get audio clips only
  const audioClips = clips.filter(c => {
    const asset = assetsById.get(c.assetId);
    return asset?.type === 'audio';
  });

  // FIX: Pre-compute which assets have active clips
  // This prevents the bug where processing multiple clips with the same assetId
  // causes the last clip's state to override previous clips' states
  const assetsWithActiveClips = new Set<string>();
  const activeClipByAsset = new Map<string, Clip>(); // Track which clip is active for each asset
  for (const clip of audioClips) {
    if (isTimeInsideClip(clip, playhead)) {
      assetsWithActiveClips.add(clip.assetId);
      activeClipByAsset.set(clip.assetId, clip);
    }
  }

  // PHASE 1: Detect voice activity for ducking
  // Voice tracks are clips WITHOUT ducking enabled (they ARE the voice)
  let voiceActive = false;
  for (const clip of audioClips) {
    const asset = assetsById.get(clip.assetId);
    if (!asset) continue;

    const isActive = isTimeInsideClip(clip, playhead);
    if (!isActive || !isPlaying) continue;

    // Voice tracks don't have ducking enabled (they're the foreground)
    const hasDucking = clip.audio?.ducking ?? false;
    if (hasDucking) continue; // Skip music/background tracks

    // Check if this voice track is actually playing
    const audio = audioMap.get(asset.id);
    if (audio && detectVoiceActivity(audio, 0.1)) {
      voiceActive = true;
      break;
    }
  }

  for (const clip of audioClips) {
    const asset = assetsById.get(clip.assetId);
    if (!asset || asset.type !== 'audio') continue;

    const isActive = isTimeInsideClip(clip, playhead);

    // FIX: Skip this clip if it's not active but another clip with the same assetId IS active
    // This prevents the "last clip wins" bug when multiple clips share the same audio asset
    if (!isActive && assetsWithActiveClips.has(clip.assetId)) {
      // Another clip with this asset is currently active - let that one control playback
      continue;
    }

    // Get or create audio element
    const audio = getOrCreateAudioElement(asset.id, asset.src, audioMap);

    // FIX: Only add to activeAssetIds when the clip is actually active
    // This ensures the cleanup section properly handles inactive audio
    if (isActive) {
      activeAssetIds.add(asset.id);
    }
    const track = tracksById.get(clip.trackId);
    const trackMuted = !!track?.muted;
    const trackSolo = !!track?.solo;

    // Multi-element support: check if clip is in targetClipIds array
    const regionTargetsClip =
      !!regionEffect &&
      (regionEffect.targetClipIds?.includes(clip.id) ||
       (typeof regionEffect.targetClipId === "string" && regionEffect.targetClipId === clip.id));

    // Determine if audio should be muted
    const shouldMute = masterMuted || trackMuted || !!clip.muted || (hasSolo && !trackSolo);

    if (isActive && isPlaying) {
      // Calculate volume with fades
      const baseVolPct = clip.audio?.volume ?? 100;
      const normalizedVol = Math.max(0, Math.min(2, baseVolPct / 100));
      const volumeWithFades = calculateVolumeWithFades(clip, playhead, normalizedVol);

      // PHASE 1: Apply volume keyframes on top of fades
      let volumeWithKeyframes = calculateVolumeWithKeyframes(clip, playhead, volumeWithFades, keyframes);

      // Region FX volume multiplier (ramped) - avoids clicks at boundaries.
      const regionVolTarget =
        regionTargetsClip && regionEffect?.volume !== undefined ? clamp(regionEffect.volume / 100, 0, 2) : 1;
      const regionVol = getRampedValue(
        regionVolumeRamp,
        `clip:${clip.id}`,
        regionVolTarget,
        now,
        volumeRampMsFor(regionEffect),
        0.001
      );
      volumeWithKeyframes *= regionVol;

      // PHASE 1: Apply ducking if enabled on this clip
      const duckingEnabled = clip.audio?.ducking ?? false;
      if (duckingEnabled) {
        const prevDuckState = duckingState.get(clip.id) ?? { active: false, rampProgress: 0 };
        const { volume: volumeWithDucking, duckState } = applyDucking(
          volumeWithKeyframes,
          voiceActive,
          0.7, // 70% reduction
          prevDuckState
        );
        volumeWithKeyframes = volumeWithDucking;
        duckingState.set(clip.id, duckState);
      }

      // Apply mute state
      if (audio.muted !== shouldMute) {
        try { audio.muted = shouldMute; } catch {}
      }

      // Apply volume (capped at 1 for HTML5 audio)
      // Use volumeWithKeyframes which includes fades + keyframe automation + ducking
      if (!shouldMute) {
        const targetVol = Math.min(1, volumeWithKeyframes);
        if (Math.abs(audio.volume - targetVol) > 0.01) {
          try { audio.volume = targetVol; } catch {}
        }
      }

      // Calculate target time, with Region FX playback multiplier (ramped)
      const clipSpeed = clip.speed ?? 1;
      const regionSpeedTarget =
        regionTargetsClip && regionEffect?.speed !== undefined ? clamp(regionEffect.speed, 0.25, 4) : 1;
      const regionSpeed = getRampedValue(
        regionSpeedRamp,
        `clip:${clip.id}`,
        regionSpeedTarget,
        now,
        speedRampMsFor(regionEffect),
        0.001
      );
      const baseRate = Math.min(4, Math.max(0.25, clipSpeed * regionSpeed));
      const srcT = mediaTimeForClip(clip, playhead);
      const dur = Number.isFinite(asset.duration) && asset.duration > 0
        ? asset.duration
        : (audio.duration || Infinity);
      const upper = Math.min(dur, clip.out ?? dur);
      const targetTime = Math.max(clip.in, Math.min(upper, srcT));

      // Calculate drift
      const currentTime = audio.currentTime;
      const drift = Number.isFinite(currentTime) ? targetTime - currentTime : 0;
      const absDrift = Math.abs(drift);

      const lastSeek = audioSyncState.lastSeekTime.get(asset.id) ?? 0;
      const timeSinceLastSeek = now - lastSeek;

      // FIX: Critical boundary check - if audio.currentTime is outside [clip.in, upper],
      // we MUST seek immediately regardless of cooldown. This prevents split audio clips
      // from playing past their trim boundaries.
      const isOutsideBoundary = Number.isFinite(currentTime) &&
        (currentTime < clip.in - 0.05 || currentTime > upper + 0.05);

      try {
        if (audio.paused) {
          // Start playing — but only once the element has actual data.
          // Calling play() on a fresh element that is still loading
          // (readyState < HAVE_CURRENT_DATA) defers the start, and in
          // several browsers the deferred playback lands silent. Waiting
          // for HAVE_CURRENT_DATA lets the first play() produce audio.
          // Next sync frames will retry until readyState is ready.
          if (audio.readyState >= 2) {
            audio.currentTime = targetTime;
            audio.playbackRate = baseRate;
            audioSyncState.lastSeekTime.set(asset.id, now);
            audio.play().catch(err => {
              if (err?.name === 'AbortError') return;
              console.warn(`Failed to play audio ${asset.id}:`, err);
            });
          }
        } else if (isJump || isOutsideBoundary) {
          // User scrubbed/jumped OR audio has drifted outside valid range
          // FIX: Force seek when outside boundary to prevent playing deleted/trimmed sections
          audio.currentTime = targetTime;
          audio.playbackRate = baseRate;
          audioSyncState.lastSeekTime.set(asset.id, now);
        } else if (absDrift > HARD_DRIFT_TOLERANCE && timeSinceLastSeek > ((now - audioSyncState.playbackStartMs) < 500 ? 200 : SEEK_COOLDOWN_MS)) {
          // Large drift - hard seek
          audio.currentTime = targetTime;
          audio.playbackRate = baseRate;
          audioSyncState.lastSeekTime.set(asset.id, now);
        } else if (absDrift > SOFT_DRIFT_TOLERANCE) {
          // Medium drift - use playbackRate
          const correction = drift > 0 ? RATE_CORRECTION_MAX : RATE_CORRECTION_MIN;
          const adjustedRate = baseRate * correction;
          if (Math.abs(audio.playbackRate - adjustedRate) > 0.005) {
            try { audio.playbackRate = adjustedRate; } catch {}
          }
        } else {
          // Small drift - normal rate
          if (Math.abs(audio.playbackRate - baseRate) > 0.01) {
            try { audio.playbackRate = baseRate; } catch {}
          }
        }
      } catch (err) {
        // Ignore errors
      }
    } else {
      // Not active or not playing: pause and mute
      if (!audio.muted) {
        try { audio.muted = true; } catch {}
      }
      if (!audio.paused) {
        try { audio.pause(); } catch {}
      }

      // If scrubbing, seek to the correct position
      if (!isPlaying && isActive) {
        const clipSpeed = clip.speed ?? 1;
        const regionSpeedTarget =
          regionTargetsClip && regionEffect?.speed !== undefined ? clamp(regionEffect.speed, 0.25, 4) : 1;
        const regionSpeed = getRampedValue(
          regionSpeedRamp,
          `clip:${clip.id}`,
          regionSpeedTarget,
          now,
          speedRampMsFor(regionEffect),
          0.001
        );
        const baseRate = Math.min(4, Math.max(0.25, clipSpeed * regionSpeed));
        const srcT = mediaTimeForClip(clip, playhead);
        const dur = Number.isFinite(asset.duration) && asset.duration > 0
          ? asset.duration
          : (audio.duration || Infinity);
        const upper = Math.min(dur, clip.out ?? dur);
        const targetTime = Math.max(clip.in, Math.min(upper, srcT));

        const absDrift = Math.abs(audio.currentTime - targetTime);
        if (absDrift > SCRUB_TOLERANCE) {
          try { audio.currentTime = targetTime; } catch {}
        }
        if (Math.abs(audio.playbackRate - baseRate) > 0.01) {
          try { audio.playbackRate = baseRate; } catch {}
        }
      }
    }
  }

  // Pause and cleanup inactive audio elements
  for (const [assetId, audio] of audioMap.entries()) {
    if (!activeAssetIds.has(assetId)) {
      if (!audio.muted) {
        try { audio.muted = true; } catch {}
      }
      if (!audio.paused) {
        try { audio.pause(); } catch {}
      }
    }
  }
}

/**
 * Clean up audio elements that are no longer needed
 */
export function cleanupUnusedAudioElements(
  assets: Asset[],
  audioMap: AudioMap
): void {
  const audioAssetIds = new Set(
    assets.filter(a => a.type === 'audio').map(a => a.id)
  );

  for (const [assetId, audio] of audioMap.entries()) {
    if (!audioAssetIds.has(assetId)) {
      try { audio.pause(); } catch {}
      try { audio.removeAttribute('src'); audio.load(); } catch {}
      audioMap.delete(assetId);
    }
  }
}

/**
 * PHASE 1: Detect voice activity using Web Audio API
 *
 * Simple volume-based voice detection. Returns true if audio level is above threshold.
 * This is a basic implementation - production could use more sophisticated voice detection.
 *
 * @param audioElement - The HTMLAudioElement to analyze
 * @param threshold - Volume threshold (0-1 range, default 0.15)
 * @returns boolean indicating if voice/speech is detected
 */
export function detectVoiceActivity(
  audioElement: HTMLAudioElement,
  threshold: number = 0.15
): boolean {
  // Simple heuristic: if audio is playing and not muted, assume voice is active
  // More sophisticated: could use AnalyserNode to check frequency/volume
  if (audioElement.paused || audioElement.muted) return false;
  if (audioElement.volume < threshold) return false;
  return true;
}

/**
 * PHASE 1: Apply audio ducking (reduce volume when voice is present)
 *
 * Smoothly reduces music/background audio volume when voice is detected.
 * Uses gentle ramping to avoid clicks/pops.
 *
 * @param baseVolume - Original volume (0-1 range)
 * @param voiceActive - Is voice currently detected
 * @param duckAmount - How much to reduce (0-1 range, default 0.7 = 70% reduction)
 * @param previousDuckState - Previous ducking state for smooth transitions
 * @returns Adjusted volume with ducking applied
 */
export function applyDucking(
  baseVolume: number,
  voiceActive: boolean,
  duckAmount: number = 0.7,
  previousDuckState: { active: boolean; rampProgress: number } = { active: false, rampProgress: 0 }
): { volume: number; duckState: { active: boolean; rampProgress: number } } {
  const targetDuckMultiplier = voiceActive ? (1 - duckAmount) : 1;

  // Smooth ramping: 50ms attack/release
  // At 60fps, this is ~3 frames. We'll use a simple linear ramp.
  const rampSpeed = 0.05; // 5% per frame ~= 50ms at 60fps

  let currentRampProgress = previousDuckState.rampProgress;
  const targetRampProgress = voiceActive ? 1 : 0;

  // Ramp towards target
  if (currentRampProgress < targetRampProgress) {
    currentRampProgress = Math.min(1, currentRampProgress + rampSpeed);
  } else if (currentRampProgress > targetRampProgress) {
    currentRampProgress = Math.max(0, currentRampProgress - rampSpeed);
  }

  // Interpolate volume multiplier
  const duckMultiplier = 1 - (duckAmount * currentRampProgress);
  const finalVolume = baseVolume * duckMultiplier;

  return {
    volume: finalVolume,
    duckState: { active: voiceActive, rampProgress: currentRampProgress }
  };
}

/**
 * Get all active audio elements from the map
 * Useful for export to mix audio from all sources
 */
export function getActiveAudioElements(
  playhead: number,
  clips: Clip[],
  assets: Asset[],
  audioMap: AudioMap
): HTMLAudioElement[] {
  const assetsById = new Map<string, Asset>(assets.map(a => [a.id, a]));
  const activeElements: HTMLAudioElement[] = [];

  for (const clip of clips) {
    const asset = assetsById.get(clip.assetId);
    if (!asset || asset.type !== 'audio') continue;

    if (isTimeInsideClip(clip, playhead)) {
      const audio = audioMap.get(asset.id);
      if (audio) {
        activeElements.push(audio);
      }
    }
  }

  return activeElements;
}

/**
 * Reset audio sync state (call when loading a new project)
 */
export function resetAudioSyncState(): void {
  audioSyncState.lastSeekTime.clear();
  audioSyncState.prevPlayhead = 0;
  audioSyncState.activeClipIds.clear();
  audioSyncState.playbackStartMs = 0;
  // PHASE 1: Also reset ducking state
  duckingState.clear();
}

/**
 * Mark the moment playback starts. For 500ms after this, seek cooldown
 * is reduced to allow faster initial audio sync.
 */
export function markAudioPlaybackStart(): void {
  audioSyncState.playbackStartMs = performance.now();
}
